import {
  type GetRateLimitValueQuery,
  useRateLimit as useConvexRateLimit,
} from "@convex-dev/rate-limiter/react";
/**
 * Rate Limit Hook - V2.5
 *
 * Provides rate limit status for the AI chat.
 * Uses the hookAPI from @convex-dev/rate-limiter.
 *
 * Checks both:
 * - sendMessage: Per-message rate limit (1 per 5 seconds)
 * - tokenUsagePerUser: Token quota limit (2000 tokens/minute)
 */
import { api } from "@convex/_generated/api";
import { useEffect, useRef } from "react";

import { logger, PortalEvents } from "~/lib/logger";

const getRateLimitRef = api.ai.rateLimiting
  .getRateLimit as GetRateLimitValueQuery;

const getTokenRateLimitRef = api.ai.rateLimiting
  .getTokenRateLimit as GetRateLimitValueQuery;

export interface RateLimitStatus {
  /** Whether the user can send a message */
  ok: boolean;
  /** Seconds until rate limit resets (if limited) */
  retryInSeconds: number | null;
  /** Whether the rate limit is being checked */
  isLoading: boolean;
  /** Which limit is blocking (for UI messaging) */
  limitType: "message" | "tokens" | null;
}

/**
 * Hook to get rate limit status for sending messages.
 * Returns countdown timer when rate limited.
 * Checks BOTH message rate limit and token usage limit.
 */
export function useRateLimitStatus(): RateLimitStatus {
  const { status: messageStatus } = useConvexRateLimit(getRateLimitRef);
  const { status: tokenStatus } = useConvexRateLimit(getTokenRateLimitRef);

  // Track previous status for logging changes
  const previousStatusReference = useRef<{
    ok: boolean;
    limitType: "message" | "tokens" | null;
  }>({
    ok: true,
    limitType: null,
  });

  // Compute current status
  let currentStatus: RateLimitStatus;

  // Loading if either status is undefined
  if (!(messageStatus && tokenStatus)) {
    currentStatus = {
      ok: true,
      retryInSeconds: null,
      isLoading: true,
      limitType: null,
    };
  } else if (!messageStatus.ok) {
    // Check message rate limit first (more immediate)
    const retryInSeconds = Math.max(
      0,
      Math.ceil((messageStatus.retryAt - Date.now()) / 1000)
    );
    currentStatus = {
      ok: false,
      retryInSeconds,
      isLoading: false,
      limitType: "message",
    };
  } else if (tokenStatus.ok) {
    currentStatus = {
      ok: true,
      retryInSeconds: null,
      isLoading: false,
      limitType: null,
    };
  } else {
    // Check token usage rate limit
    const retryInSeconds = Math.max(
      0,
      Math.ceil((tokenStatus.retryAt - Date.now()) / 1000)
    );
    currentStatus = {
      ok: false,
      retryInSeconds,
      isLoading: false,
      limitType: "tokens",
    };
  }

  // Log rate limit status changes
  useEffect(() => {
    if (currentStatus.isLoading) {
      return;
    }

    const hasStatusChanged =
      previousStatusReference.current.ok !== currentStatus.ok ||
      previousStatusReference.current.limitType !== currentStatus.limitType;

    if (hasStatusChanged) {
      logger.info(PortalEvents.RATE_LIMIT_STATUS_CHANGED, {
        previous_ok: previousStatusReference.current.ok,
        previous_limit_type: previousStatusReference.current.limitType,
        current_ok: currentStatus.ok,
        current_limit_type: currentStatus.limitType,
        retry_in_seconds: currentStatus.retryInSeconds,
      });

      previousStatusReference.current = {
        ok: currentStatus.ok,
        limitType: currentStatus.limitType,
      };
    }
  }, [
    currentStatus.ok,
    currentStatus.limitType,
    currentStatus.retryInSeconds,
    currentStatus.isLoading,
  ]);

  return currentStatus;
}
